import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, CalendarDays, ListChecks, Target,
  Sparkles, GraduationCap, NotebookPen, LogOut, Menu, X, BookMarked, FolderOpen,
  Timer, Mail, Send, BrainCircuit,
} from "lucide-react";
import Overview from "@/components/Overview";
import Timetable from "@/components/Timetable";
import Tasks from "@/components/Tasks";
import Goals from "@/components/Goals";
import AITutor from "@/components/AITutor";
import Schedule from "@/components/Schedule";
import Notes from "@/components/Notes";
import Curriculum from "@/components/Curriculum";
import Documents from "@/components/Documents";
import ExamPrep from "@/components/ExamPrep";
import GmailSync from "@/components/GmailSync";
import ParentDigest from "@/components/ParentDigest";
import TrainAI from "@/components/TrainAI";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "timetable", label: "Timetable", icon: CalendarDays },
  { id: "tasks", label: "Tasks & Homework", icon: ListChecks },
  { id: "tutor", label: "AI Tutor", icon: Sparkles },
  { id: "examprep", label: "Exam Prep", icon: Timer },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "train", label: "Train AI", icon: BrainCircuit },
  { id: "goals", label: "Goals", icon: Target },
  { id: "schedule", label: "Exams & Events", icon: GraduationCap },
  { id: "curriculum", label: "Curriculum", icon: BookMarked },
  { id: "notes", label: "Notes", icon: NotebookPen },
  { id: "digest", label: "Parent Digest", icon: Send },
  { id: "gmail", label: "Gmail Sync", icon: Mail },
];

export default function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  const [tutorPrompt, setTutorPrompt] = useState("");

  const askTutor = (q) => { setTutorPrompt(q); setTab("tutor"); };

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long",
  });

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map((n) => {
        const Icon = n.icon;
        const active = tab === n.id;
        return (
          <button
            key={n.id}
            data-testid={`nav-${n.id}`}
            onClick={() => { setTab(n.id); setOpen(false); }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              active
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon className="w-[18px] h-[18px]" />
            {n.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-slate-200 bg-white p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 leading-tight">AyaanKiStudies</p>
            <p className="text-xs text-slate-400">Grade 9 · Neev IB</p>
          </div>
        </div>
        <NavItems />
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="mt-auto flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> Log out
        </button>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setOpen(false)}
          >
            <motion.aside
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="w-72 h-full bg-white p-5 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <p className="font-heading font-bold text-slate-900">AyaanKiStudies</p>
                <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              <NavItems />
              <button onClick={onLogout} className="mt-auto flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600">
                <LogOut className="w-[18px] h-[18px]" /> Log out
              </button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-slate-200/70 px-5 sm:px-8 py-4 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-btn">
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">{today}</p>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900 truncate">
              {NAV.find((n) => n.id === tab)?.label}
            </h1>
          </div>
          <img
            src="https://images.unsplash.com/photo-1610484826625-ac2be7f1c8c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHx0ZWVuYWdlJTIwc3R1ZGVudCUyMHN0dWR5aW5nfGVufDB8fHx8MTc4NjM0MTM2MXww&ixlib=rb-4.1.0&q=85"
            alt="Ayaan"
            className="w-10 h-10 rounded-full object-cover ml-auto ring-2 ring-white shadow"
          />
        </header>

        <div className="p-5 sm:p-8 max-w-7xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {tab === "overview" && <Overview goTo={setTab} />}
              {tab === "timetable" && <Timetable />}
              {tab === "tasks" && <Tasks />}
              {tab === "tutor" && <AITutor initialPrompt={tutorPrompt} onConsumePrompt={() => setTutorPrompt("")} />}
              {tab === "examprep" && <ExamPrep />}
              {tab === "documents" && <Documents />}
              {tab === "train" && <TrainAI />}
              {tab === "goals" && <Goals />}
              {tab === "schedule" && <Schedule />}
              {tab === "curriculum" && <Curriculum askTutor={askTutor} />}
              {tab === "notes" && <Notes />}
              {tab === "digest" && <ParentDigest />}
              {tab === "gmail" && <GmailSync />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
